/**
* 运营分析页面
*
* @author By AICode
* @date 2026-03-18
*/
<template>
  <div class="operations_container">
    <Tabs :active-key="activeTab" @change="handleTabChange">
      <template slot="tabBarExtraContent">
        <a-tooltip title="分析看板设置">
          <a-icon type="setting" class="ops-dashboard-setting-trigger" @click="openDashboardSetting" />
        </a-tooltip>
      </template>
      <TabPane v-for="tab in sheetList" :key="tab.id" :tab="tab.sheetName" />
    </Tabs>
    <DashboardSetting :visible="dashboardSettingVisible" :list="sheetList" @ok="handleDashboardSettingOk"
      @cancel="handleDashboardSettingCancel" />
    <div class="tab_content">
      <FilterCondition :key="activeTab" :table-header-data="currentTabConfig.tableHeaderData"
        :table-header-default-value="currentTabConfig.tableHeaderDefaultValue"
        :table-header-recovery-value="currentTabConfig.tableHeaderRecoveryValue"
        :filter-params="currentFilterParams"
        :has-changed="hasChanged" :sheet-limit-reached="isSheetLimitReached"
        @time-range-change="handleTimeRangeChange" @save="handleSave" @filter-change="handleFilterChange"
        @table-header-change="handleTableHeaderChange" @save-as="handleSaveAs" />
      <div class="table_wrapper">
        <a-table :columns="tableColumns" :data-source="tableData" :scroll="{ x: 'max-content' }"
          :rowKey="(record, index) => index" :pagination="pagination" :loading="tableLoading"
          @change="handleTableChange" xScrollPlacement="tableBottom" />
      </div>
    </div>
  </div>
</template>

<script>
import './index.less'

import { Tabs, Table, Tooltip, Icon } from 'ant-design-vue'
const { TabPane } = Tabs
import FilterCondition from './filterCondition/filterCondition.vue'
import DashboardSetting from './DashboardSetting/DashboardSetting.vue'
import { createNamespacedHelpers } from 'vuex'
import moment from 'moment'
const { mapState, mapActions } = createNamespacedHelpers('operation')

export default {
  name: 'Operations',
  components: {
    Tabs,
    TabPane,
    FilterCondition,
    ATable: Table,
    ATooltip: Tooltip,
    AIcon: Icon,
    DashboardSetting,
  },
  data() {
    return {
      activeTab: '',
      dashboardSettingVisible: false,
      currentFilterParams: {
        applyTimeFrom: '',
        applyTimeTo: '',
        activeTimeFrom: '',
        activeTimeTo: '',
        filters: {},
      },

      activeColumnKeys: [],
      currentSort: [],
      // 仅保存可变的翻页状态，total 由 computed pagination 从 Vuex 实时读取
      paginationState: { current: 1, pageSize: 10 },
      // 最近一次保存/加载时的快照，用于判断页面状态是否有改动 - By AICode
      savedState: null,
      tableLoading: false,
    }
  },
  async created() {
    await this.getColumnMetadata()
    const list = await this.getSheetList()
    const firstSheet = (list || [])[0] || {}
    this.activeTab = firstSheet.id || ''
    if (!firstSheet.id) {
      return
    }

    await this.loadSheetAndQueryByTab(firstSheet.id)
  },
  computed: {
    ...mapState(['metadata', 'sheetList', 'currentSheet', 'analysisData', 'analysisTotal', 'dimensionOptions']),
    dimensionMetaList() {
      return this.normalizeMetaList(this.metadata?.dimensions || [])
    },
    metricMetaList() {
      return this.normalizeMetaList(this.metadata?.metrics || [])
    },
    dimensionKeys() {
      return this.dimensionMetaList.map((item) => item.key)
    },
    metricKeys() {
      return this.metricMetaList.map((item) => item.key)
    },
    tableHeaderData() {
      return [
        [
          {
            key: 'dim',
            title: '维度',

            children: this.dimensionMetaList.map((item) => ({
              key: item.key,
              title: item.title,
            })),
          },
        ],
        [
          {
            key: 'met',
            title: '指标',
            children: this.metricMetaList.map((item) => ({
              key: item.key,
              title: item.title,
            })),
          },
        ],
      ]
    },
    tableHeaderDefaultValue() {
      return [...this.dimensionKeys, ...this.metricKeys]
    },
    currentTabConfig() {
      return {
        tableHeaderData: this.tableHeaderData,
        // sheet 已加载时以 activeColumnKeys 为选中态，否则回退到全量 key - By AICode
        tableHeaderDefaultValue: this.activeColumnKeys.length
          ? this.activeColumnKeys
          : this.tableHeaderDefaultValue,
        // 全量 key 用于「恢复默认」，始终还原到所有列全选 - By AICode
        tableHeaderRecoveryValue: this.tableHeaderDefaultValue,
      }
    },
    // sheetList 超过 20 个时禁止继续另存为 - By AICode
    isSheetLimitReached() {
      return (this.sheetList || []).length >= 20
    },
    // 筛选条件、自定义表头、排序任一改变时返回 true - By AICode
    hasChanged() {
      if (this.currentSheet?.isSystem === 1) {
        return true
      }
      if (!this.savedState) {
        return false
      }
      const filterChanged = JSON.stringify(this.currentFilterParams) !== JSON.stringify(this.savedState.filterParams)
      const columnsChanged = JSON.stringify(this.activeColumnKeys) !== JSON.stringify(this.savedState.columnKeys)
      const sortChanged = JSON.stringify(this.currentSort) !== JSON.stringify(this.savedState.sort)
      return filterChanged || columnsChanged || sortChanged
    },
    flatHeaderMap() {
      const map = {}
        ;[...this.dimensionMetaList, ...this.metricMetaList].forEach((item) => {
          map[item.key] = item.title
        })
      return map
    },
    tableColumns() {
      const currentSortItem = this.currentSort[0] || {}
      return this.activeColumnKeys.map((key) => ({
        key,
        dataIndex: key,
        title: this.flatHeaderMap[key] || key,
        customRender: (text) => text === undefined || text === null ? '--' : text,
        sorter: true,
        ellipsis: true,
        width: key === 'itemName' ? 180 : 120,
        sortOrder: currentSortItem.columnKey === key
          ? (currentSortItem.order === 'asc' ? 'ascend' : 'descend')
          : null,
      }))
    },
    tableData() {
      return this.analysisData || []
    },
    // total 直接从 Vuex 读取，避免组件重新挂载时 watch 因值未变而不触发的问题 - By AICode
    pagination() {
      return {
        current: this.paginationState.current,
        pageSize: this.paginationState.pageSize,
        total: this.analysisTotal || 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        showTotal: total => `共有 ${total} 条`
      }
    },
  },
  methods: {
    ...mapActions([
      'getSheetList',
      'saveSheet',
      'saveAsSheet',
      'syncSheets',
      'batchDeleteSheets',
      'queryAnalysisData',
      'searchDimension',
      'loadSheetConfigAndAnalysis',
      'getColumnMetadata',
    ]),
    // 切换 tab 时重置表格数据和列配置，再复用统一加载逻辑 - By AICode
    async handleTabChange(key) {
      this.activeTab = key
      this.$store.commit('operation/setAnalysisData', [])
      this.activeColumnKeys = [...this.tableHeaderDefaultValue]
      if (!key) {
        return
      }
      await this.loadSheetAndQueryByTab(key)
    },
    // 统一拉取看板配置并查询数据，切换 tab 时始终从第一页开始 - By AICode
    async loadSheetAndQueryByTab(sheetId) {
      this.paginationState = { ...this.paginationState, current: 1 }
      this.tableLoading = true
      const result = await this.loadSheetConfigAndAnalysis({
        sheetId,
        fallbackColumns: this.tableHeaderDefaultValue,
        pageNo: 1,
        pageSize: this.paginationState.pageSize,
      })
      this.tableLoading = false
      if (!result || !result.columns) {
        return
      }
      const sheetConfig = result.sheetConfig || {}
      this.currentFilterParams = {
        applyTimeFrom: sheetConfig.applyTimeFrom || '',
        applyTimeTo: sheetConfig.applyTimeTo || '',
        activeTimeFrom: sheetConfig.activeTimeFrom || '',
        activeTimeTo: sheetConfig.activeTimeTo || '',
        filters: sheetConfig.filters || {},
      }
      // 创建时间和更新时间一致时才设置默认的生效时间为30天
      if (sheetConfig.createTime === sheetConfig.updateTime) {
        this.currentFilterParams.activeTimeFrom = moment().subtract(30, 'days').format('YYYY-MM-DD HH:mm')
        this.currentFilterParams.activeTimeTo = moment().format('YYYY-MM-DD HH:mm')
      }
      this.activeColumnKeys = [...result.columns]
      this.currentSort = (result.sort && result.sort.length) ? result.sort : []
      // 加载完成后以当前状态为基准，用于后续判断是否有改动 - By AICode
      this.savedState = {
        filterParams: JSON.parse(JSON.stringify(this.currentFilterParams)),
        columnKeys: [...this.activeColumnKeys],
        sort: JSON.parse(JSON.stringify(this.currentSort)),
      }
    },
    // 将 antd sorter 结构转换为后端约定的 sort 数组 - By AICode
    normalizeTableSort(sorter) {
      const order = sorter?.order
      const columnKey = sorter?.columnKey || sorter?.field
      if (!order || !columnKey) {
        return []
      }
      return [
        {
          columnKey,
          order: order === 'ascend' ? 'asc' : 'desc',
        },
      ]
    },
    handleFilterChange(params) {
      const queryParams = params || {}
      const filters = {}

      Object.keys(queryParams).forEach((key) => {
        if (
          ['applyTimeFrom', 'applyTimeTo', 'activeTimeFrom', 'activeTimeTo', 'filters']
            .includes(key)
        ) {
          return
        }
        if (queryParams[key] !== '') {
          filters[key] = String(queryParams[key]).split(',').filter((item) => item !== '')
        }
      })
      this.currentFilterParams = {
        applyTimeFrom: queryParams.applyTimeFrom || queryParams.dateFrom || '',
        applyTimeTo: queryParams.applyTimeTo || queryParams.dateTo || '',
        activeTimeFrom: queryParams.activeTimeFrom || '',
        activeTimeTo: queryParams.activeTimeTo || '',
        filters: queryParams.filters || filters,
      }
      // 筛选条件变更时重置到第一页 - By AICode
      this.paginationState = { ...this.paginationState, current: 1 }
      const body = {
        columns: this.activeColumnKeys,
        applyTimeFrom: this.currentFilterParams.applyTimeFrom,
        applyTimeTo: this.currentFilterParams.applyTimeTo,
        activeTimeFrom: this.currentFilterParams.activeTimeFrom,
        activeTimeTo: this.currentFilterParams.activeTimeTo,
        filters: this.currentFilterParams.filters,
        sort: this.currentSort,
        pageNo: 1,
        pageSize: this.paginationState.pageSize,
      }
      this.tableLoading = true
      this.queryAnalysisData(body).finally(() => { this.tableLoading = false })
    },
    // 统一接管分页、排序事件，改为后端分页排序查询 - By AICode
    handleTableChange(paginationInfo, filters, sorter) {
      const newSort = this.normalizeTableSort(sorter)
      const sorterColumnKey = sorter?.columnKey || sorter?.field
      if (newSort.length) {
        this.currentSort = newSort
      } else if (sorterColumnKey) {
        // 用户主动点击表头取消排序，尊重空排序状态，不回退默认值 - By AICode
        this.currentSort = []
      }
      this.paginationState = {
        current: paginationInfo.current || 1,
        pageSize: paginationInfo.pageSize || this.paginationState.pageSize,
      }
      const body = {
        columns: this.activeColumnKeys,
        applyTimeFrom: this.currentFilterParams.applyTimeFrom,
        applyTimeTo: this.currentFilterParams.applyTimeTo,
        activeTimeFrom: this.currentFilterParams.activeTimeFrom,
        activeTimeTo: this.currentFilterParams.activeTimeTo,
        filters: this.currentFilterParams.filters,
        sort: this.currentSort,
        pageNo: this.paginationState.current,
        pageSize: this.paginationState.pageSize,
      }
      this.tableLoading = true
      this.queryAnalysisData(body).finally(() => { this.tableLoading = false })
    },
    // 同步子组件时间范围到父级统一状态，避免筛选状态分散在多个组件 - By AICode
    handleTimeRangeChange(range) {
      this.currentFilterParams = {
        ...this.currentFilterParams,
        ...(range || {}),
      }
    },
    handleTableHeaderChange(keys) {
      this.activeColumnKeys = keys
      // 当前排序列不在新表头中时清空排序，避免以不可见列排序 - By AICode
      const sortKey = this.currentSort[0]?.columnKey
      if (sortKey && !keys.includes(sortKey)) {
        this.currentSort = []
      }
      this.handleFilterChange(this.currentFilterParams)
    },

    // 统一构建看板保存参数，保证保存与另存为使用同一份页面配置快照 - By AICode
    buildSheetPayload(extra = {}) {
      return {
        columns: this.activeColumnKeys,
        applyTimeFrom: this.currentFilterParams.applyTimeFrom,
        applyTimeTo: this.currentFilterParams.applyTimeTo,
        activeTimeFrom: this.currentFilterParams.activeTimeFrom,
        activeTimeTo: this.currentFilterParams.activeTimeTo,
        filters: this.currentFilterParams.filters,
        sort: this.currentSort,
        ...extra,
      }
    },

    // 接收 FilterCondition 另存为看板事件 - By AICode
    async handleSaveAs(values) {
      const payload = this.buildSheetPayload({
        sheetName: (values?.name || '').trim() || '未命名分析看板',
      })
      const res = await this.saveAsSheet(payload)
      
      if (!res.sheetId) {
        return
      }
      this.$message.success('另存为成功')

      const list = await this.getSheetList()
      const createdSheetId = res?.sheetId || res?.id || (typeof res === 'string' ? res : '')
      const fallbackSheet = (list || []).find((item) => item.sheetId === res.sheetId)
      const nextSheetId = createdSheetId || fallbackSheet?.id || ''
      if (!nextSheetId) {
        return
      }
      await this.handleTabChange(nextSheetId)

    },
    // 保存当前看板配置 - By AICode
    async handleSave() {
      const payload = this.buildSheetPayload({
        sheetId: this.activeTab,
      })
      const res = await this.saveSheet(payload)
      if (!res) {
        return
      }
      this.$message.success('保存成功')
      await this.getSheetList()
      // 保存成功后更新快照，重置"已改动"状态 - By AICode
      this.savedState = {
        filterParams: JSON.parse(JSON.stringify(this.currentFilterParams)),
        columnKeys: [...this.activeColumnKeys],
        sort: JSON.parse(JSON.stringify(this.currentSort)),
      }
    },

    openDashboardSetting() {
      this.dashboardSettingVisible = true
    },
    // 同步看板设置后，若当前 tab 已被删除则自动切换到第一个 - By AICode
    async handleDashboardSettingOk(newList) {
      const sheetList = Array.isArray(newList) ? newList : []
      const res = await this.syncSheets({ items: sheetList })
      if (!res) {
        return
      }
      const updatedList = await this.getSheetList()
      this.dashboardSettingVisible = false
      const stillExists = (updatedList || []).some((item) => item.id === this.activeTab)
      if (!stillExists) {
        const firstSheet = (updatedList || [])[0] || {}
        await this.handleTabChange(firstSheet.id || '')
      }
    },
    handleDashboardSettingCancel() {
      this.dashboardSettingVisible = false
    },
    normalizeMetaList(source) {
      const list = Array.isArray(source) ? source : []
      const keySet = new Set()
      const result = []
      list.forEach((item) => {
        const key = typeof item === 'string'
          ? item
          : (item?.key || item?.field || item?.code || item?.column || item?.name || item?.id || '')
        if (!key || keySet.has(key)) {
          return
        }
        const title = typeof item === 'string'
          ? item
          : (item?.title || item?.label || item?.nameCn || item?.name || key)
        keySet.add(key)
        result.push({ key, title })
      })
      return result
    },
  },
}
</script>
